import "./application";
import {
  auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from '../utils/firebase/auth';
import {
  fetchSignInMethodsForEmail,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  OAuthProvider,
  sendSignInLinkToEmail
} from 'firebase/auth';

declare global {
  interface Window {
    claimToken: string;
    claimAvailable: boolean;
    claimAlreadyDone: boolean;
    profileUrl: string;
    welcomeUrl: string;
    prefilledEmail: string;
    claimMode: 'signin' | 'signup';
    user: any;
  }
}

const form               = document.getElementById('claim_form') as HTMLFormElement | null;
const emailInput         = document.getElementById('claim_email') as HTMLInputElement | null;
const passwordBlock      = document.getElementById('claim_password_block') as HTMLElement | null;
const passwordInput      = document.getElementById('claim_password') as HTMLInputElement | null;
const submitBtn          = document.getElementById('claim_submit') as HTMLButtonElement | null;
const submitLabel        = document.getElementById('claim_submit_label') as HTMLElement | null;
const errorBox           = document.getElementById('claim_error') as HTMLElement | null;
const forgotLink         = document.getElementById('claim_forgot_password') as HTMLAnchorElement | null;
const providerLoading    = document.getElementById('claim_provider_loading') as HTMLElement | null;
const oauthBlock         = document.getElementById('claim_oauth_block') as HTMLElement | null;
const oauthDivider       = document.getElementById('claim_oauth_divider') as HTMLElement | null;
const googleBtn          = document.getElementById('claim_google') as HTMLButtonElement | null;
const appleBtn           = document.getElementById('claim_apple') as HTMLButtonElement | null;
const emailLinkLink      = document.getElementById('claim_email_link') as HTMLAnchorElement | null;
const webviewWarning     = document.getElementById('claim_webview_warning') as HTMLElement | null;
const copyUrlBtn         = document.getElementById('claim_copy_url') as HTMLButtonElement | null;

let mode: 'signup' | 'signin' = (window.claimMode === 'signin') ? 'signin' : 'signup';

function setError(message: string, level: 'danger' | 'info' = 'danger') {
  if (!errorBox) return;
  errorBox.textContent = message;
  errorBox.classList.remove('d-none', 'alert-danger', 'alert-info');
  errorBox.classList.add(level === 'danger' ? 'alert-danger' : 'alert-info');
}

function clearError() {
  if (!errorBox) return;
  errorBox.textContent = '';
  errorBox.classList.add('d-none');
}

function show(el: HTMLElement | null)  { el?.classList.remove('d-none'); }
function hide(el: HTMLElement | null)  { el?.classList.add('d-none'); }

// All auth controls — buttons + links. Disabled wholesale during any in-flight
// sign-in attempt so a user can't kick off two parallel Firebase auth flows
// (e.g. tap Google while a password submit is mid-flight). The triggering
// control gets a spinner; everything else dims to 0.5 opacity.
function authControls(): (HTMLButtonElement | HTMLAnchorElement)[] {
  return [submitBtn, googleBtn, appleBtn, forgotLink, emailLinkLink].filter(
    (el): el is HTMLButtonElement | HTMLAnchorElement => !!el
  );
}

function lockAuth(active: HTMLElement | null, busyLabel?: string) {
  authControls().forEach((c) => {
    if (c === active) return;
    if (c instanceof HTMLButtonElement) c.disabled = true;
    c.style.pointerEvents = 'none';
    c.style.opacity = '0.5';
  });
  if (passwordInput) passwordInput.disabled = true;
  if (active instanceof HTMLButtonElement) {
    active.disabled = true;
    if (busyLabel && !active.dataset.originalHtml) {
      active.dataset.originalHtml = active.innerHTML;
      active.innerHTML = `<span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>${busyLabel}`;
    }
  }
}

function unlockAuth(active: HTMLElement | null) {
  authControls().forEach((c) => {
    if (c instanceof HTMLButtonElement) c.disabled = false;
    c.style.pointerEvents = '';
    c.style.opacity = '';
  });
  if (passwordInput) passwordInput.disabled = false;
  if (active instanceof HTMLButtonElement && active.dataset.originalHtml) {
    active.innerHTML = active.dataset.originalHtml;
    delete active.dataset.originalHtml;
  }
}

// Detects Facebook/Instagram/TikTok/etc. in-app webviews. These typically block
// OAuth popups and redirects to Google, so we surface an "open in browser"
// callout when we know the user needs OAuth to sign in.
function isInAppBrowser(): boolean {
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|FB_IAB|FB4A|Instagram|BytedanceWebview|TikTok|MicroMessenger|Line\/|; wv\)/i.test(ua);
}

async function sendAccessTokenToRails() {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('No Firebase ID token after auth');
  const response = await fetch(`/firebase/auth.json?accessToken=${encodeURIComponent(idToken)}`, {
    credentials: 'same-origin'
  });
  if (!response.ok) throw new Error(`Rails auth sync failed (${response.status})`);
}

async function finalizeClaimOnRails(): Promise<{ claimed: boolean; reason: string; redirect_url?: string }> {
  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';
  const response = await fetch('/purchases/claim', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken
    },
    body: JSON.stringify({ claim_token: window.claimToken })
  });
  return response.json().catch(() => ({ claimed: false, reason: 'parse_error' }));
}

function nextUrlAfterClaim(claim: { redirect_url?: string }): string {
  return claim.redirect_url || window.welcomeUrl || window.profileUrl || '/';
}

async function completeAfterFirebaseAuth() {
  await sendAccessTokenToRails();
  const claim = await finalizeClaimOnRails();
  if (claim.claimed || claim.reason === 'already_claimed') {
    window.location.href = nextUrlAfterClaim(claim);
  } else {
    window.location.reload();
  }
}

// Provider detection — runs once on load for returning users so we can render
// the right sign-in controls (password / Google / Apple / email link).
async function detectProvidersAndRenderUI() {
  if (mode !== 'signin' || !window.prefilledEmail) {
    hide(providerLoading);
    return;
  }
  let methods: string[] = [];
  try {
    methods = await fetchSignInMethodsForEmail(auth, window.prefilledEmail);
  } catch (e) {
    methods = [];
  }
  hide(providerLoading);

  const hasPassword = methods.length === 0 || methods.includes('password');
  const hasGoogle   = methods.includes('google.com');
  const hasApple    = methods.includes('apple.com');
  const oauthOnly   = !hasPassword && (hasGoogle || hasApple);

  if (hasPassword) {
    show(passwordBlock);
    show(submitBtn);
    show(forgotLink);
  }
  if (hasGoogle) { show(googleBtn); show(oauthBlock); }
  if (hasApple)  { show(appleBtn);  show(oauthBlock); }
  if (hasPassword && (hasGoogle || hasApple)) show(oauthDivider);

  // If we couldn't determine any providers (some Firebase projects enable
  // email-enumeration protection), surface the email-link option as a safety
  // net so users with OAuth-only accounts still have a path forward.
  if (methods.length === 0) show(emailLinkLink);

  // Webview warning only matters when OAuth is required.
  if (oauthOnly && isInAppBrowser()) show(webviewWarning);
}

async function handleSubmit(event: Event) {
  event.preventDefault();
  if (!emailInput || !passwordInput) return;
  clearError();

  const email = emailInput.value.trim();
  const password = passwordInput.value;

  if (!email) { setError('Email is missing.'); return; }
  if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }

  lockAuth(submitBtn, mode === 'signup' ? 'Activating…' : 'Signing in…');

  try {
    if (mode === 'signup') {
      try {
        await createUserWithEmailAndPassword(auth, email, password);
      } catch (err: any) {
        if (err?.code === 'auth/email-already-in-use') {
          unlockAuth(submitBtn);
          mode = 'signin';
          if (submitLabel) submitLabel.textContent = 'Log in & activate';
          show(forgotLink);
          setError("Looks like you already have a HiiKER account with this email. Enter your existing password to activate your subscription.");
          return;
        }
        throw err;
      }
    } else {
      await signInWithEmailAndPassword(auth, email, password);
    }

    await completeAfterFirebaseAuth();
  } catch (err: any) {
    console.error('Claim activation failed', err);
    const code = err?.code as string | undefined;
    if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
      setError("That password didn't match. Try again, use a sign-in option below, or reset your password.");
      show(forgotLink);
    } else if (code === 'auth/weak-password') {
      setError('Password must be at least 6 characters.');
    } else if (code === 'auth/too-many-requests') {
      setError('Too many attempts. Try again in a few minutes or reset your password.');
      show(forgotLink);
    } else {
      setError(err?.message || 'Something went wrong. Try again or contact support@hiiker.com.');
    }
    unlockAuth(submitBtn);
  }
}

async function handleOAuth(provider: 'google' | 'apple') {
  clearError();
  const btn = provider === 'google' ? googleBtn : appleBtn;
  if (isInAppBrowser()) {
    show(webviewWarning);
    setError('In-app browsers block Google and Apple sign-in. Open this page in your default browser to continue.');
    return;
  }
  lockAuth(btn, 'Signing in…');
  try {
    const authProvider =
      provider === 'google'
        ? new GoogleAuthProvider()
        : (() => { const p = new OAuthProvider('apple.com'); p.addScope('email'); p.addScope('name'); return p; })();
    if (provider === 'google') {
      (authProvider as GoogleAuthProvider).setCustomParameters({ login_hint: window.prefilledEmail || '' });
    }

    try {
      await signInWithPopup(auth, authProvider);
    } catch (popupErr: any) {
      // Fall back to redirect if the popup was blocked. We deliberately leave
      // controls locked here — the page is about to navigate to the OAuth IDP.
      if (popupErr?.code === 'auth/popup-blocked' || popupErr?.code === 'auth/popup-closed-by-user') {
        await signInWithRedirect(auth, authProvider);
        return;
      }
      throw popupErr;
    }

    await completeAfterFirebaseAuth();
  } catch (err: any) {
    console.error('OAuth claim failed', err);
    setError(err?.message || `Could not sign in with ${provider}. Try again or use a different option.`);
    unlockAuth(btn);
  }
}

async function handleForgotPassword(event: Event) {
  event.preventDefault();
  if (!emailInput) return;
  const email = emailInput.value.trim();
  if (!email) return;
  try {
    await sendPasswordResetEmail(auth, email);
    setError(`Password reset email sent to ${email}. It can take a minute to arrive — check your spam folder if you don't see it, then come back here.`, 'info');
  } catch (err: any) {
    setError(err?.message || 'Could not send reset email.');
  }
}

async function handleEmailLink(event: Event) {
  event.preventDefault();
  if (!emailInput) return;
  const email = emailInput.value.trim();
  if (!email) return;
  lockAuth(emailLinkLink);
  try {
    await sendSignInLinkToEmail(auth, email, {
      url: window.location.href,
      handleCodeInApp: true
    });
    window.localStorage.setItem('emailForSignIn', email);
    setError(`Sign-in link sent to ${email}. Open it on this same device to finish activating your subscription.`, 'info');
  } catch (err: any) {
    setError(err?.message || 'Could not send sign-in link.');
  } finally {
    unlockAuth(emailLinkLink);
  }
}

async function handleCopyUrl() {
  try {
    await navigator.clipboard.writeText(window.location.href);
    if (copyUrlBtn) {
      const original = copyUrlBtn.textContent;
      copyUrlBtn.textContent = 'Copied — paste it in your browser';
      setTimeout(() => { if (copyUrlBtn && original) copyUrlBtn.textContent = original; }, 2500);
    }
  } catch {
    /* noop */
  }
}

// Already signed in (e.g. recovery-email click while authenticated) — kick the
// claim immediately and bounce.
async function autoClaimIfSignedIn() {
  if (!window.user || !window.claimAvailable || window.claimAlreadyDone) return;
  const claim = await finalizeClaimOnRails();
  if (claim.claimed || claim.reason === 'already_claimed') {
    window.location.href = nextUrlAfterClaim(claim);
  } else {
    window.location.reload();
  }
}

if (form)         form.addEventListener('submit', handleSubmit);
if (forgotLink)   forgotLink.addEventListener('click', handleForgotPassword);
if (emailLinkLink) emailLinkLink.addEventListener('click', handleEmailLink);
if (googleBtn)    googleBtn.addEventListener('click', () => handleOAuth('google'));
if (appleBtn)     appleBtn.addEventListener('click', () => handleOAuth('apple'));
if (copyUrlBtn)   copyUrlBtn.addEventListener('click', handleCopyUrl);

detectProvidersAndRenderUI();
autoClaimIfSignedIn();
